import type {
  FieldValues,
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
  IssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import type {EntityOperation} from '@github-ui/optimistic-connection'

export type OptimisticIssueFieldValue = {
  __typename:
    | 'IssueFieldTextValue'
    | 'IssueFieldSingleSelectValue'
    | 'IssueFieldMultiSelectValue'
    | 'IssueFieldDateValue'
    | 'IssueFieldNumberValue'
  field: {id: string; name: string}
  value?: string | null
  optionId?: string | null
  name?: string | null
  color?: IssueFieldSingleSelectOption['color'] | null
  description?: string | null
  options?: Array<{
    id?: string
    name: string
    color?: IssueFieldSingleSelectOption['color'] | null
    description?: string | null
  }>
  numberValue?: number | null
}

export type IssueFieldValueMutationRoute = {
  owner: string
  repository: string
  number: number
}

export type IssueFieldValueMutationInput = {
  issueId: string
  issueField: {fieldId: string} & FieldValues
}

export function upsertIssueFieldValue(issueId: string, value: OptimisticIssueFieldValue): EntityOperation {
  return {
    type: 'upsertEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: value.field.id,
    item: value,
  }
}

export function removeIssueFieldValue(issueId: string, fieldId: string, fieldName: string): EntityOperation {
  return {
    type: 'removeEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: fieldId,
    item: {field: {id: fieldId, name: fieldName}},
  }
}

export function toOptimisticIssueFieldValue(
  fieldId: string,
  fieldName: string,
  fieldType: IssueFieldType,
  selection: IssueFieldSelectionValues,
): OptimisticIssueFieldValue | undefined {
  const field = {id: fieldId, name: fieldName}
  switch (fieldType) {
    case 'text':
      return typeof selection === 'string' ? {__typename: 'IssueFieldTextValue', field, value: selection} : undefined
    case 'single_select': {
      const option = selection as IssueFieldSingleSelectOption | null
      return option && typeof option.name === 'string'
        ? {
            __typename: 'IssueFieldSingleSelectValue',
            field,
            optionId: option.id,
            name: option.name,
            color: option.color,
            description: option.description ?? null,
          }
        : undefined
    }
    case 'multi_select': {
      if (!Array.isArray(selection) || !selection.every(option => typeof option.name === 'string')) return undefined
      return {
        __typename: 'IssueFieldMultiSelectValue',
        field,
        options: selection.map(option => ({
          id: option.id,
          name: option.name,
          color: option.color,
          description: option.description ?? null,
        })),
      }
    }
    case 'date':
      return selection instanceof Date
        ? {__typename: 'IssueFieldDateValue', field, value: selection.toISOString()}
        : undefined
    case 'number':
      return typeof selection === 'number'
        ? {__typename: 'IssueFieldNumberValue', field, numberValue: selection}
        : undefined
  }
}

export function normalizeIssueFieldValue(
  value: unknown,
  optimisticValue: OptimisticIssueFieldValue,
): OptimisticIssueFieldValue | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const {__typename, field} = optimisticValue

  switch (__typename) {
    case 'IssueFieldTextValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof record.name === 'string' || record.name === null
        ? {
            __typename,
            field,
            optionId:
              typeof record.optionId === 'string' || record.optionId === null
                ? record.optionId
                : (optimisticValue.optionId ?? null),
            name: record.name,
            color:
              typeof record.color === 'string' || record.color === null
                ? (record.color as IssueFieldSingleSelectOption['color'] | null)
                : (optimisticValue.color ?? null),
            description:
              typeof record.description === 'string' || record.description === null
                ? record.description
                : (optimisticValue.description ?? null),
          }
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(record.options)) return undefined
      const names = record.options.map(option =>
        option && typeof option === 'object' ? (option as Record<string, unknown>).name : undefined,
      )
      return names.every((name): name is string => typeof name === 'string')
        ? {
            __typename,
            field,
            options: names.map(name => ({name})),
          }
        : undefined
    }
    case 'IssueFieldDateValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof record.numberValue === 'number' || record.numberValue === null
        ? {__typename, field, numberValue: record.numberValue}
        : undefined
  }
}
